import { motion, Transition } from 'framer-motion'
import {
	createElement,
	useEffect,
	useRef,
	useState,
	useMemo,
	useCallback,
	type ElementType,
} from 'react'

type BlurTextProps = {
	text?: string
	delay?: number
	className?: string
	animateBy?: 'words' | 'letters'
	direction?: 'top' | 'bottom'
	threshold?: number
	rootMargin?: string
	animationFrom?: Record<string, string | number>
	animationTo?: Array<Record<string, string | number>>
	easing?: (t: number) => number
	onAnimationComplete?: () => void
	stepDuration?: number
	/** Etiqueta semántica del título (p. ej. h2 en secciones). Por defecto p. */
	as?: ElementType
	id?: string
}

const buildKeyframes = (
	from: Record<string, string | number>,
	steps: Array<Record<string, string | number>>,
): Record<string, Array<string | number>> => {
	const keys = new Set<string>([...Object.keys(from), ...steps.flatMap((s) => Object.keys(s))])

	const keyframes: Record<string, Array<string | number>> = {}
	keys.forEach((k) => {
		keyframes[k] = [from[k], ...steps.map((s) => s[k])]
	})
	return keyframes
}

const BlurText: React.FC<BlurTextProps> = ({
	text = '',
	delay = 200,
	className = '',
	animateBy = 'words',
	direction = 'top',
	threshold = 0.1,
	rootMargin = '0px',
	animationFrom,
	animationTo,
	easing = (t) => t,
	onAnimationComplete,
	stepDuration = 0.35,
	as: Component = 'p',
	id,
}) => {
	const [inView, setInView] = useState(false)
	const ref = useRef<HTMLElement>(null)

	const defaultFrom = useMemo(() => {
		return direction === 'top'
			? { filter: 'blur(10px)', opacity: 0, y: -50 }
			: { filter: 'blur(10px)', opacity: 0, y: 50 }
	}, [direction])

	const defaultTo = useMemo(() => {
		return [
			{
				filter: 'blur(5px)',
				opacity: 0.5,
				y: direction === 'top' ? 5 : -5,
			},
			{ filter: 'blur(0px)', opacity: 1, y: 0 },
		]
	}, [direction])

	const fromSnapshot = animationFrom ?? defaultFrom
	const toSnapshots = animationTo ?? defaultTo

	const elements = useMemo(() => {
		return animateBy === 'words' ? text.split(' ') : text.split('')
	}, [text, animateBy])

	const stepCount = toSnapshots.length + 1
	const totalDuration = stepDuration * (stepCount - 1)
	const times = useMemo(() => {
		return Array.from({ length: stepCount }, (_, i) => (stepCount === 1 ? 0 : i / (stepCount - 1)))
	}, [stepCount])

	const animateKeyframes = useMemo(() => {
		return buildKeyframes(fromSnapshot, toSnapshots)
	}, [fromSnapshot, toSnapshots])

	const handleIntersection = useCallback(([entry]: IntersectionObserverEntry[]) => {
		if (entry.isIntersecting) {
			setInView(true)
			if (ref.current) {
				observer.unobserve(ref.current)
			}
		}
	}, [])

	const observer = useMemo(() => {
		return new IntersectionObserver(handleIntersection, { threshold, rootMargin })
	}, [handleIntersection, threshold, rootMargin])

	useEffect(() => {
		if (!ref.current) return

		observer.observe(ref.current)

		return () => {
			observer.disconnect()
		}
	}, [observer])

	const handleAnimationComplete = useCallback(() => {
		onAnimationComplete?.()
	}, [onAnimationComplete])

	const content = elements.map((segment, index) => {
		const spanTransition: Transition = {
			duration: totalDuration,
			times,
			delay: (index * delay) / 1000,
			ease: easing,
		}

		return (
			<motion.span
				key={`${segment}-${index}`}
				initial={fromSnapshot}
				animate={inView ? animateKeyframes : fromSnapshot}
				transition={spanTransition}
				onAnimationComplete={index === elements.length - 1 ? handleAnimationComplete : undefined}
				style={{
					display: 'inline-block',
					willChange: inView ? 'transform, filter, opacity' : 'auto',
				}}
			>
				{segment === ' ' ? '\u00A0' : segment}
				{animateBy === 'words' && index < elements.length - 1 && '\u00A0'}
			</motion.span>
		)
	})

	return createElement(
		Component,
		{
			ref,
			id,
			className: `blur-text ${className} flex flex-wrap justify-center`,
		},
		content,
	)
}

export default BlurText
